<template>
    <MainContent title="Liberar Asiento">
        <LiberarAsientoSkeleton v-if="loading" />

        <div v-else>
            <!-- Sin eventos -->
            <Card v-if="!eventos || eventos.length === 0">
                <div class="tw:text-center tw:py-8">
                    <h4 class="tw:text-lg tw:font-semibold tw:text-gray-700">No hay eventos</h4>
                    <p class="tw:text-gray-500">No hay eventos para este abonado</p>
                </div>
            </Card>

            <!-- Grid de eventos -->
            <div v-else class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-4">
                <Card
                    v-for="evento in eventos"
                    :key="evento.id"
                    padding="0"
                    customClasses="tw:overflow-hidden tw:hover:shadow-md tw:hover:border-gray-300"
                >
                    <!-- Cabecera del partido -->
                    <div class="tw:bg-gradient-to-b tw:from-gray-50 tw:to-white tw:border-b tw:border-gray-100 tw:px-4 tw:pt-4 tw:pb-3">
                        <div
                            v-if="evento.equipo_local || evento.equipo_visitante"
                            class="tw:flex tw:items-start tw:justify-center tw:gap-2"
                        >
                            <div class="tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:flex-1 tw:min-w-0">
                                <img
                                    v-if="evento.equipo_local?.imagen"
                                    :src="evento.equipo_local.imagen"
                                    :alt="evento.equipo_local.nombre"
                                    class="tw:h-12 tw:w-12 tw:object-contain tw:rounded-lg tw:bg-white tw:border tw:border-gray-200 tw:p-1"
                                />
                                <span class="tw:text-xs tw:font-semibold tw:text-gray-800 tw:text-center tw:leading-tight tw:line-clamp-2">
                                    {{ evento.equipo_local?.nombre || '—' }}
                                </span>
                            </div>
                            <div class="tw:flex tw:flex-col tw:items-center tw:pt-3 tw:px-1">
                                <span class="tw:text-[10px] tw:font-black tw:text-gray-400 tw:tracking-widest">VS</span>
                            </div>
                            <div class="tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:flex-1 tw:min-w-0">
                                <img
                                    v-if="evento.equipo_visitante?.imagen"
                                    :src="evento.equipo_visitante.imagen"
                                    :alt="evento.equipo_visitante.nombre"
                                    class="tw:h-12 tw:w-12 tw:object-contain tw:rounded-lg tw:bg-white tw:border tw:border-gray-200 tw:p-1"
                                />
                                <span class="tw:text-xs tw:font-semibold tw:text-gray-800 tw:text-center tw:leading-tight tw:line-clamp-2">
                                    {{ evento.equipo_visitante?.nombre || '—' }}
                                </span>
                            </div>
                        </div>
                        <h5 v-else class="tw:font-semibold tw:text-gray-800 tw:text-center tw:mb-2">
                            {{ evento.name || 'EVENTO SIN NOMBRE' }}
                        </h5>

                        <!-- Fecha y hora -->
                        <div class="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:mt-3">
                            <span class="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-2.5 tw:py-1 tw:rounded-full tw:bg-gray-100 tw:text-xs tw:font-medium tw:text-gray-600">
                                <i class="fas fa-calendar-alt tw:text-gray-400"></i>
                                {{ evento.fecha }}
                            </span>
                            <span class="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-2.5 tw:py-1 tw:rounded-full tw:bg-gray-100 tw:text-xs tw:font-medium tw:text-gray-600">
                                <i class="fas fa-clock tw:text-gray-400"></i>
                                {{ evento.hora }}
                            </span>
                        </div>
                    </div>

                    <!-- Sesiones -->
                    <div class="tw:px-4 tw:py-3">
                        <p class="tw:text-[11px] tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wider tw:mb-2">
                            Sesiones
                        </p>

                        <div v-if="!evento.asientos_liberados" class="tw:space-y-2">
                            <label
                                v-for="sesion in evento.sessiones"
                                :key="sesion.id"
                                class="tw:flex tw:items-center tw:gap-2.5 tw:px-3 tw:py-2 tw:rounded-lg tw:border tw:cursor-pointer tw:transition-all tw:duration-150 tw:text-sm"
                                :class="[
                                    sesion.asiento_liberado
                                        ? 'tw:opacity-50 tw:cursor-not-allowed tw:bg-gray-50 tw:border-gray-200'
                                        : isSesionSelected(evento, sesion)
                                            ? 'tw:border-blue-500 tw:bg-blue-50 tw:ring-1 tw:ring-blue-500'
                                            : 'tw:border-gray-200 tw:bg-white hover:tw:border-gray-300 hover:tw:bg-gray-50',
                                ]"
                            >
                                <input
                                    type="radio"
                                    :value="sesion"
                                    :disabled="sesion.asiento_liberado"
                                    v-model="selectedSesionByEvent[evento.id]"
                                    class="tw:sr-only"
                                />
                                <span
                                    class="tw:h-4 tw:w-4 tw:rounded-full tw:border-2 tw:shrink-0 tw:flex tw:items-center tw:justify-center tw:transition-colors tw:duration-150"
                                    :class="isSesionSelected(evento, sesion) ? 'tw:border-blue-500' : 'tw:border-gray-300'"
                                >
                                    <span
                                        class="tw:h-2 tw:w-2 tw:rounded-full tw:bg-blue-500 tw:transition-opacity tw:duration-150"
                                        :class="isSesionSelected(evento, sesion) ? 'tw:opacity-100' : 'tw:opacity-0'"
                                    ></span>
                                </span>
                                <span class="tw:flex-1 tw:min-w-0">
                                    <span class="tw:block tw:font-medium tw:text-gray-800" :class="sesion.asiento_liberado ? 'tw:line-through' : ''">
                                        {{ sesion.fecha }}
                                    </span>
                                    <span class="tw:block tw:text-xs tw:text-gray-500">
                                        {{ sesion.hora }}
                                    </span>
                                </span>
                                <span
                                    v-if="sesion.asiento_liberado"
                                    class="tw:inline-flex tw:items-center tw:gap-1 tw:px-2 tw:py-0.5 tw:rounded-full tw:bg-gray-200 tw:text-[10px] tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide"
                                >
                                    <i class="fas fa-lock"></i>
                                    Liberada
                                </span>
                            </label>
                        </div>

                        <div
                            v-else
                            class="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:px-3 tw:py-3 tw:rounded-lg tw:bg-green-50 tw:border tw:border-green-200 tw:text-sm tw:font-medium tw:text-green-700"
                        >
                            <i class="fas fa-check-circle"></i>
                            Asiento liberado en todos los partidos
                        </div>
                    </div>

                    <!-- Acción -->
                    <div class="tw:px-4 tw:py-3 tw:bg-gray-50 tw:border-t tw:border-gray-100">
                        <Button
                            v-if="!evento.asientos_liberados"
                            variant="primary"
                            custom-class="tw:w-full tw:justify-center"
                            :disabled="!selectedSesionByEvent[evento.id]"
                            @click="handleLiberar(evento, selectedSesionByEvent[evento.id])"
                        >
                            <i class="fas fa-unlock-alt tw:mr-2"></i>
                            Liberar asiento
                        </Button>
                        <div
                            v-else
                            class="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-sm tw:font-semibold tw:text-green-700"
                        >
                            <i class="fas fa-check-circle"></i>
                            Asiento Liberado
                        </div>
                    </div>
                </Card>
            </div>

            <!-- Modal de confirmación (siempre montado, vue-js-modal controla visibilidad) -->
            <LiberarAsientoModal
                :abonado_id="abonado_id"
                :evento="eventoSelected"
                :sesion="sesionSelected"
                @close="onModalClose"
            />
        </div>
    </MainContent>
</template>

<script setup>
    import { reactive, getCurrentInstance } from 'vue';
    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import LiberarAsientoSkeleton from '@/modules/admin/abonados/components/LiberarAsientoSkeleton.vue';
    import LiberarAsientoModal from '@/modules/admin/abonados/modals/LiberarAsientoModal.vue';
    import { useLiberarAsiento } from '@/composables/admin/abonados/useLiberarAsiento';

    const props = defineProps({
        lista_id: { type: [String, Number], required: true },
        abonado_id: { type: [String, Number], required: true },
    });

    const instance = getCurrentInstance();

    const {
        eventos,
        loading,
        eventoSelected,
        sesionSelected,
        selectForLiberar,
        onModalClose,
    } = useLiberarAsiento(props);

    // Track selected session per event (keyed by evento.id)
    const selectedSesionByEvent = reactive({});

    function isSesionSelected(evento, sesion) {
        return selectedSesionByEvent[evento.id]?.id === sesion.id;
    }

    function handleLiberar(evento, sesion) {
        if (selectForLiberar(evento, sesion)) {
            instance.proxy.$modal.show('liberarAsiento');
        }
    }
</script>
